/**
 * @file Champ de saisie de numéro de téléphone avec sélecteur de pays.
 * @module packages.common.components.phone-input.PhoneInput
 *
 * TextField MUI avec sélecteur de pays intégré en InputAdornment.
 * Normalisation automatique des numéros au format E.164.
 *
 * Le pays est la propriété de l'utilisateur : une fois choisi dans le sélecteur
 * (ou déduit d'un numéro complet), il n'est plus jamais réécrit automatiquement.
 * Ce point est critique pour les indicatifs partagés par plusieurs pays
 * (+1 pour US/CA/Caraïbes, +44, +39, +7) où la librairie ne peut identifier le
 * pays qu'une fois le numéro complet : toute déduction intermédiaire renverrait
 * une valeur indéterminée et ferait sauter le sélecteur.
 *
 * Le découpage indicatif/numéro national et le formatage à l'écran sont délégués
 * à libphonenumber-js (AsYouType), seule source de vérité des règles par pays
 * (préfixe interurbain, zéro initial significatif ou non, groupement des chiffres).
 */

import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import {TextField, InputAdornment} from '@mui/material';
import {AsYouType, getCountryCallingCode, getCountries, getExampleNumber, isValidPhoneNumber} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import {detectUserCountry} from '../../utils/country-detection';
import CountrySelect from './CountrySelect';

/**
 * Indicatif téléphonique d'un pays, sans jamais lever d'exception.
 *
 * @param {string} country - Code pays ISO 3166-1 alpha-2
 * @return {string} Indicatif sans le "+", chaîne vide si le pays est inconnu
 */
const callingCodeOf = (country) => {
  try {
    return country ? getCountryCallingCode(country) : '';
  } catch (error) {
    return '';
  }
};

/**
 * Convertit une saisie en chiffres nationaux pour le pays sélectionné.
 *
 * Délègue à AsYouType le retrait de l'indicatif et du préfixe interurbain :
 * le "0" de tête disparaît en Belgique ou en France, mais reste conservé en
 * Italie où il fait partie du numéro.
 *
 * @param {string} digits - Chiffres saisis, sans séparateurs
 * @param {string} country - Code pays ISO sélectionné
 * @return {string} Chiffres du numéro national
 */
const toNationalDigits = (digits, country) => {
  if (!digits) return '';
  const formatter = new AsYouType(country);
  formatter.input(digits);
  const parsed = formatter.getNumber();
  return parsed ? parsed.nationalNumber : digits;
};

/**
 * Formate le numéro national pour l'affichage, selon les règles du pays.
 *
 * Le formatage passe par la forme internationale puis retire l'indicatif, ce
 * qui garantit un groupement correct même quand le numéro national s'écrit
 * normalement avec un préfixe interurbain absent de la saisie.
 *
 * @param {string} nationalNumber - Chiffres du numéro national
 * @param {string} country - Code pays ISO sélectionné
 * @return {string} Numéro national formaté
 */
const formatNationalDigits = (nationalNumber, country) => {
  if (!nationalNumber) return '';
  const prefix = `+${callingCodeOf(country)}`;
  const formatted = new AsYouType().input(`${prefix}${nationalNumber}`);
  return (formatted.startsWith(prefix) ? formatted.slice(prefix.length) : formatted).trim();
};

/**
 * Choisit le pays à afficher pour un indicatif donné.
 *
 * Les indicatifs partagés (+1, +44, +39, +7) restent ambigus tant que le numéro
 * est incomplet. On préserve alors la sélection en cours, puis le pays par
 * défaut, avant de retomber sur un pays de cet indicatif : dans tous les cas la
 * valeur E.164 produite reste exacte, seul le drapeau peut être provisoire.
 *
 * @param {string} callingCode - Indicatif sans le "+"
 * @param {string} currentCountry - Pays actuellement sélectionné
 * @param {string} defaultCountry - Pays par défaut du composant
 * @return {string|null} Code pays ISO, ou null si l'indicatif est inconnu
 */
const resolveCountryForCallingCode = (callingCode, currentCountry, defaultCountry) => {
  if (!callingCode) return null;
  if (callingCodeOf(currentCountry) === callingCode) return currentCountry;
  if (callingCodeOf(defaultCountry) === callingCode) return defaultCountry;
  return getCountries().find((iso) => callingCodeOf(iso) === callingCode) || null;
};

/**
 * PhoneInput - Composant de saisie de numéro de téléphone avec sélection de pays
 *
 * Au montage, et tant que l'utilisateur n'a pas touché au champ, le pays est
 * détecté via géolocalisation IP avec repli sur defaultCountry.
 *
 * @component
 * @param {Object} props
 * @param {string} props.value - Numéro au format E.164 ("+32475123456")
 * @param {Function} props.onChange - Callback avec la valeur E.164
 * @param {Function} [props.onBlur] - Callback onBlur pour react-hook-form
 * @param {boolean} [props.error] - État d'erreur
 * @param {string} [props.helperText] - Texte d'aide
 * @param {string} [props.label='Téléphone'] - Label du champ
 * @param {string} [props.defaultCountry='BE'] - Pays par défaut si détection échoue
 * @param {boolean} [props.autoDetectCountry=true] - Activer la détection automatique du pays
 * @param {boolean} [props.disabled] - Désactiver le champ
 * @return {JSX.Element} The rendered phone input component.
 */
const PhoneInput = ({value, onChange, onBlur, error = false, helperText = '', label = 'Téléphone', defaultCountry = 'BE', disabled = false, autoDetectCountry = true, ...props}) => {
  const [country, setCountry] = useState(defaultCountry);
  const [nationalNumber, setNationalNumber] = useState('');

  // Dernière valeur émise vers le parent : sert à ignorer le retour de notre
  // propre valeur, qui sinon serait reparsée à chaque frappe. La sentinelle
  // null garantit que la valeur présente au montage est bien traitée.
  const emittedValueRef = useRef(null);
  // Le pays est verrouillé dès que l'utilisateur le choisit ou qu'un numéro
  // complet l'a désigné : plus aucune détection automatique ne peut l'écraser.
  const countryLockedRef = useRef(false);

  /**
   * Émet la valeur E.164 correspondant au couple pays/numéro national.
   *
   * @param {string} nextCountry - Code pays ISO
   * @param {string} nextNational - Chiffres du numéro national
   */
  const emit = useCallback((nextCountry, nextNational) => {
    const nextValue = nextNational ? `+${callingCodeOf(nextCountry)}${nextNational}` : '';
    emittedValueRef.current = nextValue;
    onChange(nextValue);
  }, [onChange]);

  // Détection du pays au montage, sans jamais contredire un choix explicite
  useEffect(() => {
    if (!autoDetectCountry || countryLockedRef.current || value) return;

    let cancelled = false;
    detectUserCountry({useIP: true, useLanguage: false, defaultCountry})
        .then((detectedCountry) => {
          if (cancelled || countryLockedRef.current || !detectedCountry) return;
          setCountry(detectedCountry);
        })
        .catch(() => {
          // Détection indisponible : le pays par défaut reste en place
        });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDetectCountry, defaultCountry]);

  // Synchronisation sur une valeur venue du parent (édition, réinitialisation)
  useEffect(() => {
    const incoming = value || '';
    if (incoming === emittedValueRef.current) return;
    emittedValueRef.current = incoming;

    if (!incoming) {
      setNationalNumber('');
      return;
    }

    const formatter = new AsYouType();
    formatter.input(incoming);
    const detectedCountry = formatter.getCountry();
    const resolvedCountry = detectedCountry || resolveCountryForCallingCode(formatter.getCallingCode(), country, defaultCountry);

    if (resolvedCountry) {
      setCountry(resolvedCountry);
      countryLockedRef.current = true;
    }

    const parsed = formatter.getNumber();
    setNationalNumber(parsed ? parsed.nationalNumber : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry) => {
    countryLockedRef.current = true;
    setCountry(newCountry);
    emit(newCountry, nationalNumber);
  };

  const handleNumberChange = (e) => {
    const rawInput = e.target.value;
    const digits = rawInput.replace(/\D/g, '');

    // Saisie ou collage d'un numéro international : l'indicatif tapé fait foi
    if (/^\s*(\+|00)/.test(rawInput) && digits) {
      const international = `+${digits.replace(/^00/, '')}`;
      const formatter = new AsYouType();
      formatter.input(international);

      const detectedCountry = formatter.getCountry();
      const resolvedCountry = detectedCountry || resolveCountryForCallingCode(formatter.getCallingCode(), country, defaultCountry);
      const parsed = formatter.getNumber();

      if (resolvedCountry) {
        countryLockedRef.current = true;
        setCountry(resolvedCountry);
        setNationalNumber(parsed ? parsed.nationalNumber : '');
        emit(resolvedCountry, parsed ? parsed.nationalNumber : '');
        return;
      }
    }

    const national = toNationalDigits(digits, country);
    setNationalNumber(national);
    emit(country, national);
  };

  // Placeholder issu d'un vrai numéro d'exemple du pays sélectionné
  const placeholder = useMemo(() => {
    try {
      const exampleNumber = getExampleNumber(country, examples);
      if (exampleNumber) {
        return formatNationalDigits(exampleNumber.nationalNumber, country);
      }
    } catch (error) {
      // Pays sans exemple disponible
    }
    return '';
  }, [country]);

  return (
    <TextField
      {...props}
      value={formatNationalDigits(nationalNumber, country)}
      onChange={handleNumberChange}
      onBlur={onBlur}
      label={label}
      error={error}
      helperText={helperText}
      fullWidth
      disabled={disabled}
      type="tel"
      inputProps={{inputMode: 'tel', autoComplete: 'tel'}}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <CountrySelect value={country} onChange={handleCountryChange} />
          </InputAdornment>
        ),
      }}
      placeholder={placeholder}
    />
  );
};

PhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  defaultCountry: PropTypes.string,
  autoDetectCountry: PropTypes.bool,
  disabled: PropTypes.bool,
};

/**
 * Valide un numéro de téléphone au format E.164.
 *
 * @param {string} phone - Numéro de téléphone au format E.164
 * @return {boolean} true si le numéro est valide
 */
export const validatePhone = (phone) => {
  if (!phone) return false;
  try {
    return isValidPhoneNumber(phone);
  } catch (error) {
    return false;
  }
};

export default PhoneInput;
